
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useCourseStore from '../stores/useCourseStore';
import LMSHeader from '../components/LMSHeader';
import useAuthStore from '../stores/useAuthStore';
import CourseCard from '../components/CourseCard';
import DOMPurify from 'dompurify';
import { useToast } from '../components/ToastContainer';
import PaystackModal from '../components/PaystackModal';
import axiosClient from '../services/api';
import SEOLayout from '../layouts/SEOLayout';
import StarRating from '../components/StarRating';
import Hls from 'hls.js';


const SocialShare = ({ course }) => {
  const [copied, setCopied] = useState(false);

  const shareUrl = `${window.location.origin}/course/${course?.slug}/${course?._id}`;
  const shareText = encodeURIComponent(`Check out this course: ${course?.title} on BoleumLearn!`);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const sharePlatforms = [
    {
      name: 'X',
      icon: '𝕏',
      url: `https://twitter.com/intent/tweet?text=${shareText}&url=${encodeURIComponent(shareUrl)}`
    },
    {
      name: 'LinkedIn',
      icon: '🔗',
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`
    },
    {
      name: 'Facebook',
      icon: '📘',
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`
    },
    {
      name: 'WhatsApp',
      icon: '💬',
      url: `https://wa.me/?text=${shareText}%20${encodeURIComponent(shareUrl)}`
    }
  ];

  return (
    <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
      <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">Share this course</h3>
      <div className="flex flex-wrap gap-3 mb-4">
        {sharePlatforms?.map(platform => (
          <a
            key={platform?.name}
            href={platform?.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center px-3 py-2 bg-neutral-100 hover:bg-neutral-200 dark:bg-neutral-700 dark:hover:bg-neutral-600 rounded-lg text-sm font-medium text-neutral-700 dark:text-neutral-300 transition-colors"
          >
            <span className="mr-2">{platform?.icon}</span>
            {platform?.name}
          </a>
        ))}
      </div>
      <div className="flex">
        <input
          type="text"
          value={shareUrl}
          readOnly
          className="flex-1 px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-l-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white"
        />
        <button
          onClick={handleCopyLink}
          className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-r-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          {copied ? 'Copied!' : 'Copy Link'}
        </button>
      </div>
    </div>
  );
};

const CourseDetails = () => {
  const { slug, id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [isInWishlist, setIsInWishlist] = useState(false);

  const user = useAuthStore(state => state.user);
  const course = useCourseStore(state => state.course);
  const enrolledCourses = useCourseStore(state => state.enrolledCourses);
  const fetchMyCourses = useCourseStore(state => state.fetchMyCourses);
  const isLoading = useCourseStore(state => state.isLoading);
  const error = useCourseStore(state => state.error);
  const clearError = useCourseStore(state => state.clearError);
  const wishlist = useCourseStore(state => state.wishlist);
  const recentlyViewed = useCourseStore(state => state.recentlyViewed);
  const [activeModule, setActiveModule] = useState(null);

  const [isEnrolled, setIsEnrolled] = useState(false);

  const [isPaystackModalOpen, setIsPaystackModalOpen] = useState(false);
  const [paystackData, setPaystackData] = useState(null);

  const [couponCode, setCouponCode] = useState('');
  const [discount, setDiscount] = useState(null);
  const [errorCoupon, setErrorCoupon] = useState('');

  const [playbackPosition, setPlaybackPosition] = useState(0);
  const [videoUrl, setVideoUrl] = useState('');
  const [isVideoLoading, setVideoIsLoading] = useState(false);
  const [resolution, setResolution] = useState(() => {
    return localStorage.getItem('preferredResolution') || 'auto';
  });
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState('');
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const REFRESH_INTERVAL = 80 * 1000;
  const refreshTimerRef = useRef(null);

  const hasAddedToRecentlyViewed = useRef(false);

  const {
    fetchCourse,
    addToWishlist,
    removeFromWishlist,
    getWishlist,
    addToRecentlyViewed,
    getRecentlyViewed,
    enrollInCourse
  } = useCourseStore();

  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (error) {
      toast.error(error);
      clearError();
    }
  }, [error, toast]);

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    setIsSubmittingReview(true);
    try {
      await axiosClient.post(`/review/${course._id}/reviews`, {
        rating: reviewRating,
        comment: reviewComment
      });
      fetchCourse(course._id);
      setReviewComment('');
      toast.success('Thank you for your review!');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to submit review');
    } finally {
      setIsSubmittingReview(false);
    }
  };

  const getSignedUrl = async (isRefresh = false) => {
    try {
      const videoId = course?.introVideo;
      if (!videoId) return;

      setVideoIsLoading(true);
      const res = await axiosClient.get(`/videos/learn/guest/${videoId}/signed-url/${resolution}`);
      const newUrl = res.data?.data?.originalUrl;
      if (!newUrl) throw new Error('Original URL not found');

      if (isRefresh && videoRef.current) {
        const currentTime = videoRef.current.currentTime;
        const isPlaying = !videoRef.current.paused;
        attachVideoSource(newUrl, true, currentTime, isPlaying);
      } else {
        attachVideoSource(newUrl);
      }
    } catch (err) {
      console.error(`Error fetching ${resolution} URL:`, err);
    } finally {
      setVideoIsLoading(false);
    }
  };
  const attachVideoSource = (url, isRefresh = false, currentTime = 0, isPlaying = false) => {
    const video = videoRef.current;
    if (!video) return;

    // Cleanup previous HLS
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    setVideoUrl(url);

    if (Hls.isSupported() && url.includes('.m3u8')) {
      const hls = new Hls({ enableWorker: true });
      hls.loadSource(url);
      hls.attachMedia(video);
      hls.on(Hls.Events.ERROR, (event, data) => {
        if (data.fatal) hls.destroy();
      });
      hlsRef.current = hls;
    } else if (video.canPlayType('application/vnd.apple.mpegurl') && url.includes('.m3u8')) {
      // Safari native HLS
      video.src = url;
    } else {
      // Direct MP4 (Cloudinary)
      video.src = url;
    }

    if (isRefresh) {
      video.currentTime = currentTime;
      if (isPlaying) video.play().catch(() => { });
    }
  };


  useEffect(() => {
    if (course?.introVideo) {
      getSignedUrl();
      refreshTimerRef.current = setInterval(() => getSignedUrl(true), REFRESH_INTERVAL);
    }
    return () => {
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [course?.introVideo, resolution, REFRESH_INTERVAL]);

  useEffect(() => {
    fetchCourse(id)
    if (slug && id && user?.role === "student") {
      if (!hasAddedToRecentlyViewed.current) {
        addToRecentlyViewed(id);
        hasAddedToRecentlyViewed.current = true;
      }

      const loadCourseData = async () => {
        const myCourses = await fetchMyCourses();
        if (myCourses && myCourses.some(c => c._id === id)) {
          setIsEnrolled(true);
        }
      };

      loadCourseData();
      getWishlist();
      getRecentlyViewed();
    }

    return () => {
      hasAddedToRecentlyViewed.current = false;
    };
  }, [slug, id, user, addToRecentlyViewed, fetchCourse, fetchMyCourses, getRecentlyViewed, getWishlist]);

  useEffect(() => {
    if (user?.role === "student" && course?._id && wishlist) {
      setIsInWishlist(wishlist?.some(c => c?._id === course?._id));
    }
  }, [course, wishlist, user?.role]);

  const handleWishlistToggle = async () => {
    try {
      if (isInWishlist) {
        await removeFromWishlist(course?._id);
        toast.error('Course removed from wishlist');
      } else {
        await addToWishlist(course?._id);
        toast.success('Course added to wishlist');
      }

      await getWishlist();

      const updatedWishlist = useCourseStore.getState().wishlist;
      setIsInWishlist(updatedWishlist?.some(c => c?._id === course?._id));

    } catch (error) {
      console.error("Failed to toggle wishlist:", error);
    }
  };

  const applyCoupon = async () => {
    try {
      const response = await axiosClient.post('/coupons/validate', {
        code: couponCode,
        courseId: course._id
      });
      setDiscount(response.data);
      setErrorCoupon('');
    } catch (err) {
      setErrorCoupon(err.response?.data?.error || 'Invalid coupon');
      setDiscount(null);
    }
  };

  const finalPrice = discount ? discount.discountedPrice : course?.price;

  const handleEnroll = async (courseSlug, courseId) => {
    if (!user) {
      localStorage.setItem('redirectAfterLogin', `/course/${courseSlug}/${courseId}`);
      toast.info('Please login or create a student account to continue');
      setTimeout(() => navigate('/login'), 100);
      return;
    }

    try {
      const result = await enrollInCourse(courseId);
      if (result?.data?.enrollment?.paymentReference) {
        setPaystackData({
          email: user.email,
          amount: course?.price * 100,
          reference: result.data.enrollment.paymentReference,
        });
        setIsPaystackModalOpen(true);
      }
    } catch (error) {
      console.error('Error enrolling in course:', error);
    }
  };

  const handleResolutionChange = (newRes) => {
    setResolution(newRes);
    localStorage.setItem('preferredResolution', newRes);
  };

  const handleVideoProgress = () => {
    if (videoRef.current) {
      const position = videoRef.current.currentTime;
      setPlaybackPosition(position);
    }
  };

  const dynamicSEO = {
    title: course?.title || "",
    description: DOMPurify.sanitize(course?.description || "").replace(/<[^>]+>/g, ""),
    image: course?.thumbnail
      ? `${process.env.REACT_APP_SOCKET_URL}${course.thumbnail}`
      : undefined,
    keywords: course?.category?.name || "",
  };


  if (isLoading || !course) {
    return (
      <>
        <LMSHeader />
        <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <LMSHeader />
        <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">
              Course not found
            </h2>
            <button
              onClick={() => navigate(-1)}
              className="text-primary-600 dark:text-primary-400 hover:underline"
            >
              Go back
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <LMSHeader />
      <SEOLayout dynamicSEO={dynamicSEO}>
        <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 py-8">
          <div className="max-w-7xl mx-auto px-4">
            {/* Course Header */}
            <div className="mb-8">
              <button
                onClick={() => navigate(-1)}
                className="text-primary-600 dark:text-primary-400 hover:underline mb-4 flex items-center"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                </svg>
                Back
              </button>

              <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] xl:grid-cols-[280px_2fr_1fr] gap-6 w-full">
                {/* Thumbnail — full width on mobile, fixed width on xl */}
                <div className="xl:order-1">
                  <img
                    src={`${process.env.REACT_APP_SOCKET_URL}${course?.thumbnail}`}
                    alt={course?.title}
                    className="w-full h-48 object-cover rounded-lg border border-neutral-200 dark:border-neutral-700 lg:h-56 xl:h-full xl:w-72"
                  />
                </div>

                {/* Course Info — middle on desktop, top on mobile/tablet */}
                <div className="xl:order-2">
                  <h1 className="text-2xl md:text-3xl font-bold text-neutral-900 dark:text-white mb-2">
                    {course?.title}
                  </h1>
                  <div className="flex items-center mb-4">
                    <div>
                      <p className="text-lg font-medium text-neutral-900 dark:text-white">
                        {course?.instructor?.name}
                      </p>
                      <p className="text-xs text-neutral-600 dark:text-neutral-400">
                        Course Instructor
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2 md:gap-4 mb-4">
                    <span className="px-3 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-sm rounded-full">
                      {course?.category?.name}
                    </span>
                    <span className="px-3 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-sm rounded-full">
                      {course?.level}
                    </span>
                    <span className="px-3 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-sm rounded-full">
                      {course?.duration}
                    </span>

                    {user && user.role === "student" && (
                      <button
                        onClick={handleWishlistToggle}
                        className={`p-2 rounded-full ${isInWishlist
                          ? 'text-danger-500 dark:text-danger-400 bg-danger-50 dark:bg-danger-900/20'
                          : 'text-neutral-500 dark:text-neutral-400 hover:text-danger-500 dark:hover:text-danger-400'
                          }`}
                        aria-label={isInWishlist ? "Remove from wishlist" : "Add to wishlist"}
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>

                <div className="xl:order-3 flex flex-col lg:items-end">

                  {isEnrolled ? (
                    <button
                      onClick={() => navigate(`/student/learning/${course?._id}`)}
                      className="w-full max-w-xs py-3 px-6 bg-success-500 hover:bg-success-600 text-white font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-success-500 focus:ring-offset-2 dark:focus:ring-offset-neutral-800 transition-all duration-200"
                    >
                      Continue Learning
                    </button>
                  ) : (
                    <>
                      {course?.price > 0 ? (
                        <div className="mt-4 space-y-3">
                          {/* Coupon Input */}
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={couponCode}
                              onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                              placeholder="Enter coupon code"
                              className="flex-1 px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                            />
                            <button
                              onClick={applyCoupon}
                              className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
                            >
                              Apply
                            </button>
                          </div>
                          {errorCoupon && <p className="text-red-500 text-sm">{errorCoupon}</p>}
                          {discount && (
                            <div className="text-sm text-green-600">
                              Coupon applied! You save ₦
                              {Number(discount.discountAmount).toLocaleString('en-NG', {
                                minimumFractionDigits: 2
                              })}
                            </div>
                          )}
                          <button
                            onClick={() =>
                              handleEnroll(course?._id, course._id, discount?.coupon?.code)
                            }
                            className="w-full lg:max-w-xs py-3 px-6 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-neutral-800 transition-all duration-200"
                          >
                            Pay Now: ₦
                            {Number(finalPrice).toLocaleString('en-NG', {
                              minimumFractionDigits: 2
                            })}
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleEnroll(course?.slug, course?._id)}
                          className="w-full lg:max-w-xs py-3 px-6 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-neutral-800 transition-all duration-200"
                        >
                          Enroll for Free
                        </button>
                      )}
                    </>
                  )}

                  <div className="flex items-center mb-4 lg:justify-end w-full lg:w-auto">
                    <svg className="w-5 h-5 text-yellow-500 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                    <span className="text-lg font-medium text-neutral-900 dark:text-white mr-2">
                      {course?.rating || 0}
                    </span>
                    <span className="text-sm text-neutral-600 dark:text-neutral-400">
                      ({course?.reviews?.length || 0} reviews)
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="mb-8">
              <div className="border-b border-neutral-200 dark:border-neutral-700">
                <nav className="-mb-px flex space-x-8">
                  {['overview', 'curriculum', 'instructor', 'reviews'].map(tab => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`py-4 px-1 border-b-2 font-medium text-sm capitalize ${activeTab === tab
                        ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                        : 'border-transparent text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300 hover:border-neutral-300 dark:hover:border-neutral-600'
                        }`}
                    >
                      {tab}
                    </button>
                  ))}
                </nav>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                {activeTab === 'overview' && (
                  <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                    <h2 className="text-2xl font-semibold text-neutral-900 dark:text-white mb-6">
                      Course Description
                    </h2>
                    <div className="prose dark:prose-invert max-w-none">

                      <div className=" text-neutral-500 dark:text-neutral-400 mt-1 "><div
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(course?.description) }}
                      /></div>

                      {/* What you'll learn */}
                      {course?.whatYouWillLearn && course?.whatYouWillLearn?.length > 0 && (
                        <div className="bg-white dark:bg-neutral-800 rounded-lg my-5">
                          <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">
                            What you'll learn
                          </h3>
                          <ul className="space-y-2">
                            {course?.whatYouWillLearn?.map((item, index) => (
                              <li key={index} className="flex items-start">
                                <svg className="w-5 h-5 text-success-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                </svg>
                                <span className="text-neutral-600 dark:text-neutral-400">
                                  {item}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Requirements */}
                      {course?.requirements && course?.requirements?.length > 0 && (
                        <div className="bg-white dark:bg-neutral-800 rounded-lg my-5">
                          <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">
                            Requirements
                          </h3>
                          <ul className="space-y-2">
                            {course?.requirements?.map((item, index) => (
                              <li key={index} className="flex items-start">
                                <svg className="w-5 h-5 text-primary-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                                </svg>
                                <span className="text-neutral-600 dark:text-neutral-400">
                                  {item}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {activeTab === 'curriculum' && (
                  <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                    <h2 className="text-2xl font-semibold text-neutral-900 dark:text-white mb-6">
                      Course Curriculum
                    </h2>

                    <div className="space-y-4">
                      {course?.modules?.map((module, moduleIndex) => (
                        <div key={module?._id} className="border border-neutral-200 dark:border-neutral-700 rounded-lg">
                          <button
                            onClick={() => setActiveModule(activeModule === module?._id ? null : module?._id)}
                            className="w-full p-4 text-left flex justify-between items-center focus:outline-none"
                          >
                            <div>
                              <h3 className="font-medium text-neutral-900 dark:text-white">
                                Module {moduleIndex + 1}: {module?.title}
                              </h3>
                              <p className="text-sm text-neutral-600 dark:text-neutral-400">
                                {module.lessons?.length || 0} lessons
                              </p>
                            </div>
                            <svg
                              className={`w-5 h-5 text-neutral-500 dark:text-neutral-400 transition-transform duration-200 ${activeModule === module?._id ? 'rotate-180' : ''
                                }`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>

                          {activeModule === module?._id && (
                            <div className="border-t border-neutral-200 dark:border-neutral-700 p-4">
                              <div className="space-y-3">
                                {module?.lessons?.map((lesson, lessonIndex) => (
                                  <div
                                    key={lesson?._id}
                                    className="flex items-center p-3 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700 transition"
                                  >
                                    <div className="w-8 h-8 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mr-3">
                                      <span className="text-sm font-medium text-primary-600 dark:text-primary-400">
                                        {lessonIndex + 1}
                                      </span>
                                    </div>
                                    <div className="flex-1">
                                      <h4 className="font-medium text-neutral-900 dark:text-white">
                                        {lesson.title}
                                      </h4>
                                      {lesson.description && (
                                        <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-1">
                                          {lesson.description}
                                        </p>
                                      )}
                                    </div>
                                    <svg className="w-5 h-5 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                    </svg>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {activeTab === 'instructor' && (
                  <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                    <h2 className="text-2xl font-semibold text-neutral-900 dark:text-white mb-6">
                      Instructor
                    </h2>

                    <div className="flex items-center mb-6">
                      <div>
                        <h3 className="text-xl font-semibold text-neutral-900 dark:text-white">
                          {course?.instructor?.name}
                        </h3>
                        <p className="text-neutral-600 dark:text-neutral-400 mb-2 text-sm">
                          Course Instructor
                        </p>
                        {course?.instructor?.bio && (
                          <p className="text-neutral-600 dark:text-neutral-400">
                            {course?.instructor?.bio}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="text-center p-4 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg">
                        <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                          {course?.totalEnrollments || 0}
                        </div>
                        <div className="text-sm text-neutral-600 dark:text-neutral-400">
                          Students
                        </div>
                      </div>

                      <div className="text-center p-4 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg">
                        <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                          {course?.modules?.reduce((total, module) => total + module?.lessons?.length || 0, 0) || 0}
                        </div>
                        <div className="text-sm text-neutral-600 dark:text-neutral-400">
                          Lessons
                        </div>
                      </div>

                      <div className="text-center p-4 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg">
                        <div className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                          {course?.rating || 0}
                        </div>
                        <div className="text-sm text-neutral-600 dark:text-neutral-400">
                          Rating
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'reviews' && (
                  <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                    <h2 className="text-2xl font-semibold text-neutral-900 dark:text-white mb-6">
                      Reviews
                    </h2>

                    {course?.reviews?.length > 0 ? (
                      <div className="space-y-6">
                        {course?.reviews?.map((review, index) => (
                          <div key={index} className="border-b border-neutral-200 dark:border-neutral-700 pb-6 last:border-0">
                            <div className="flex items-center mb-4">
                              <img
                                src={`${process.env.REACT_APP_SOCKET_URL}/public${review?.user?.avatar}`}
                                alt={review?.user?.name}
                                className="w-10 h-10 rounded-full mr-3"
                              />
                              <div>
                                <h4 className="font-medium text-neutral-900 dark:text-white">
                                  {review?.user?.name}
                                </h4>
                                <div className="flex items-center">
                                  <span className="text-sm text-neutral-600 dark:text-neutral-400">
                                    <StarRating rating={review.rating} />
                                  </span>
                                </div>
                              </div>
                            </div>
                            <p className="text-neutral-600 dark:text-neutral-400">
                              {review?.comment}
                            </p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <div className="text-4xl mb-4">⭐</div>
                        <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-2">
                          No reviews yet
                        </h3>
                        <p className="text-neutral-600 dark:text-neutral-400">
                          Be the first to review this course!
                        </p>
                      </div>
                    )}

                    {isEnrolled && (
                      <div className="mt-8 pt-6 border-t border-neutral-200 dark:border-neutral-700">
                        <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-4">Write a Review</h3>
                        <form onSubmit={handleSubmitReview} className="space-y-4">
                          {/* Rating */}
                          <div>
                            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                              Your Rating
                            </label>
                            <div className="flex items-center">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <button
                                  key={star}
                                  type="button"
                                  onClick={() => setReviewRating(star)}
                                  className="text-2xl focus:outline-none"
                                  aria-label={`Rate ${star} stars`}
                                >
                                  {star <= (reviewRating || 0) ? (
                                    <span className="text-yellow-500">★</span>
                                  ) : (
                                    <span className="text-neutral-300 dark:text-neutral-600">☆</span>
                                  )}
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* Comment */}
                          <div>
                            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                              Your Review
                            </label>
                            <textarea
                              value={reviewComment}
                              onChange={(e) => setReviewComment(e.target.value)}
                              placeholder="Share your experience with this course..."
                              rows={4}
                              className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                              required
                            />
                          </div>

                          {/* Submit Button */}
                          <button
                            type="submit"
                            disabled={isSubmittingReview}
                            className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
                          >
                            {isSubmittingReview ? 'Submitting...' : 'Submit Review'}
                          </button>
                        </form>
                      </div>
                    )}
                  </div>
                )}

              </div>

              {/* Sidebar */}
              <div className="lg:col-span-1">
                <div className="space-y-6">
                  {course?.introVideo && (
                    <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                      <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">
                        Intro Video
                      </h3>
                      <div className="relative bg-black rounded-lg overflow-hidden mb-6">
                        <div className="absolute top-3 left-3 z-20 flex gap-2">
                          {['auto', '240p', '360p', '480p', '720p', '1080p'].map((res) => (
                            <button
                              key={res}
                              onClick={() => handleResolutionChange(res)}
                              className={`px-2 py-1 text-xs sm:text-sm font-medium rounded ${resolution === res
                                ? 'bg-primary-600 text-white'
                                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                                }`}
                            >
                              {res}
                            </button>
                          ))}
                        </div>


                        {isVideoLoading && (
                          <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-60 z-10">
                            <div className="w-10 h-10 border-4 border-gray-200 border-t-primary-500 rounded-full animate-spin" />
                          </div>
                        )}

                        <video
                          ref={videoRef}
                          src={videoUrl}
                          controls
                          controlsList="nodownload"
                          disablePictureInPicture
                          onContextMenu={(e) => e.preventDefault()}
                          className="w-full max-h-[80vh] rounded-lg select-none pointer-events-auto"
                          onTimeUpdate={handleVideoProgress}
                          onLoadedData={() => setVideoIsLoading(false)}
                        />
                      </div>

                    </div>
                  )}

                  <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
                    <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">
                      Course Stats
                    </h3>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <span className="text-neutral-600 dark:text-neutral-400">Lessons</span>
                        <span className="font-medium text-neutral-900 dark:text-white">
                          {course?.modules?.reduce((total, module) => total + module.lessons?.length || 0, 0) || 0}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-neutral-600 dark:text-neutral-400">Duration</span>
                        <span className="font-medium text-neutral-900 dark:text-white">
                          {course?.duration}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-neutral-600 dark:text-neutral-400">Students</span>
                        <span className="font-medium text-neutral-900 dark:text-white">
                          {course?.totalEnrollments || 0}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-neutral-600 dark:text-neutral-400">Rating</span>
                        <div className="flex items-center">
                          <span className="font-medium text-neutral-900 dark:text-white">
                            <StarRating rating={course?.rating || 0} />
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <SocialShare course={course} />
                </div>
              </div>
            </div>

            {recentlyViewed?.map((recentCourse) => (
              <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6 my-8">
                <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-6">Recently Viewed Courses</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  <CourseCard
                    key={recentCourse?._id}
                    course={recentCourse}
                    onEnroll={() => handleEnroll(recentCourse.course?.slug, recentCourse.course?._id)}
                    isEnrolled={enrolledCourses.some(c => c._id === recentCourse._id) ? true : false}
                  />
                </div>
              </div>
            ))}

          </div>
        </div>

        {isPaystackModalOpen && (
          <PaystackModal
            isOpen={isPaystackModalOpen}
            onClose={() => setIsPaystackModalOpen(false)}
            transactionData={paystackData}
            onSuccess={(reference) => navigate(`/payment-verification?reference=${reference}&courseId=${id}`)}
            onError={(msg) => alert(msg)}
          />
        )}

      </SEOLayout>
    </>

  );
};

export default CourseDetails;

